// @flow
import type { ExperienceMap } from 'app/common/lib/react/experiences/experienceUtils';
import { addDays, isoDateIsLater, isoDateSubtract } from 'app/common/lib/react/dateUtils';

const DEFAULT_DAYS_IN_USER_STATE = 28;

export type UserStateAttributeProps = {
  createdAt: string,
  resurrectionInfo?: {
    resurrectionDate?: string,
  },
  isPartner: boolean,
};

type UserStateProps = {
  user: UserStateAttributeProps,
  days?: number,
};

export function getNameComponents(nameInput: string) {
  const nameComponents = {};
  const fullName = nameInput.trim();
  if (fullName.length > 0) {
    nameComponents.full_name = fullName;
  }

  // TODO (sophia) Improve name processing
  // For now, split full name into first name and last name by first occurence of space.
  const firstLastNames = fullName.split(/ (.+)/);
  if (firstLastNames[0] === '') {
    firstLastNames.shift();
  }
  if (firstLastNames.length > 0) {
    nameComponents.first_name = firstLastNames[0] || '';
  }
  if (firstLastNames.length > 1) {
    nameComponents.last_name = firstLastNames[1] || '';
  }
  return nameComponents;
}

// TODO(dalvarez): update to use `created_at` too, which comes from UserResource
export function isNewUser({ user, days = DEFAULT_DAYS_IN_USER_STATE }: UserStateProps): boolean {
  // Calculating new user, who's been here less than X days
  if (!user || !user.createdAt) {
    return false;
  }
  const createdDate = new Date(user.createdAt);
  const subtractedDate = isoDateSubtract(-days, createdDate);
  const today = new Date();
  return !isoDateIsLater(today, subtractedDate);
}

export function isResurrectedUser({
  user,
  days = DEFAULT_DAYS_IN_USER_STATE,
}: UserStateProps): boolean {
  // Calculating resurrected user, who's been resurrected less than X days
  if (!user.resurrectionInfo) {
    return false;
  }
  // We count a user as resurrected if they are a resurrection candidate (today is day 0 of their resurrection) or
  // if they resurrected in the past X days
  const resurrectionDateInMS = user.resurrectionInfo.resurrectionDate;
  const today = new Date();
  const resurrectedBeginDate = resurrectionDateInMS ? new Date(resurrectionDateInMS) : today;
  const resurrectedEndDate = addDays(resurrectedBeginDate, days);
  return isoDateIsLater(resurrectedEndDate, today);
}

export function isInImageOnly(userObject: UserStateProps) {
  return isNewUser(userObject) || (isResurrectedUser(userObject) && !userObject.user.isPartner);
}

export function isInLfvImageOnly(eligibleExperiences: ExperienceMap): boolean {
  return (
    eligibleExperiences &&
    (eligibleExperiences[PCONST.thrift.Placement.WEB_PIN_GRID_ATTRIBUTION] &&
      eligibleExperiences[PCONST.thrift.Placement.WEB_PIN_GRID_ATTRIBUTION].experience_id ===
        PCONST.thrift.Experience.WEB_IMAGE_ONLY_PIN_GRID)
  );
}
